import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo} from 'react';
import {toast} from 'react-toastify';

import {
  useAddWorkflowsAdminMutation,
  useDeleteWorkflowsAdminMutation,
  WorkflowsAdminType,
} from '../../../generated/graphql';
import {TeamBaseEntity} from '../../../graphql/teamQuery';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {trackModelRegistryAccessControlsInteraction} from '../../../util/navigation';
import {TeamMemberRole} from './utils';

export interface ModelRegistryAdminRadioProps {
  entity: TeamBaseEntity;
  isViewerAdmin: boolean;
  userId: string;
  refetch?: () => void;
}

export const ModelRegistryAdminSwitch: React.FC<
  ModelRegistryAdminRadioProps
> = ({refetch, entity, userId, isViewerAdmin}) => {
  const [addModelRegistryAdmin] = useAddWorkflowsAdminMutation({
    context: propagateErrorsContext(),
    onCompleted: refetch,
  });

  const [deleteModelRegistryAdmin] = useDeleteWorkflowsAdminMutation({
    context: propagateErrorsContext(),
    onCompleted: refetch,
  });

  const viewer = useViewer();
  const isViewerModelRegistryAdmin = useMemo(() => {
    return (
      isViewerAdmin ||
      entity.workflowsAdmins.find(admin => admin.id === viewer?.id) != null
    );
  }, [entity.workflowsAdmins, isViewerAdmin, viewer]);

  const isMemberTeamAdmin = useMemo(() => {
    return (
      entity.members.find(m => m.id === userId)?.memberRole.name ===
      TeamMemberRole.ADMIN
    );
  }, [entity.members, userId]);
  const isMemberRegistryAdmin = useMemo(() => {
    return (
      isMemberTeamAdmin ||
      entity.workflowsAdmins.find(admin => admin.id === userId) != null
    );
  }, [entity.workflowsAdmins, isMemberTeamAdmin, userId]);

  return (
    <Tailwind>
      <Switch.Root
        checked={isMemberRegistryAdmin}
        disabled={!isViewerModelRegistryAdmin || isMemberTeamAdmin}
        onClick={async () => {
          try {
            if (isViewerModelRegistryAdmin) {
              if (!isMemberRegistryAdmin) {
                await addModelRegistryAdmin({
                  variables: {
                    userID: userId,
                    entityName: entity.name,
                    adminType: WorkflowsAdminType.Model,
                  },
                });
                trackModelRegistryAccessControlsInteraction({
                  event: 'add_model_registry_admin',
                });
                refetch?.();
                toast('Added model registry admin.');
              } else {
                await deleteModelRegistryAdmin({
                  variables: {
                    userID: userId,
                    entityName: entity.name,
                    adminType: WorkflowsAdminType.Model,
                  },
                });
                if (refetch != null) {
                  refetch();
                }
                toast('Removed model registry admin.');
              }
            } else {
              toast(
                'Only Model Registry/Team Admins are allowed to add or remove model registry admins.'
              );
            }
          } catch (error) {
            console.error(error);
            toast(
              'Something went wrong while trying to add/remove model registry admin.'
            );
          }
        }}>
        <Switch.Thumb checked={isMemberRegistryAdmin} />
      </Switch.Root>
    </Tailwind>
  );
};
